import {
  addEdge,
  Background,
  BackgroundVariant,
  Connection,
  ConnectionLineType,
  EdgeTypes,
  NodeTypes,
  ReactFlow,
  useEdgesState,
  useNodesState,
  useReactFlow,
  useUpdateNodeInternals,
} from "@xyflow/react"
import "@xyflow/react/dist/style.css"
import Panels from "./components/Panels"
import "@xyflow/react/dist/style.css"
import BaseNode, { BaseNodeType } from "./components/nodes/Base.node"
import ConnectionLine from "./components/ConnectionLine"
import Edge, { BaseEdgeType } from "./components/Edge"
import BaseDrawer from "./components/drawers/Base.drawer"
import { getNewEdgeProps } from "./components/flow.utils"
import GhostNode from "./components/nodes/Ghost.node"
import useScenarioHelpers from "./useScenarioHelpers"
import { store } from "@/stores/stores"
import EdgeRemoveButton from "./components/EdgeRemoveButton"
import { useSnapshot } from "valtio"
import { Scenario } from "@/gen"
import { useParams } from "react-router"
import { cn } from "@/lib/utils"

export interface ScenarioCanvasProps {
  scenario: Scenario
  nodes: BaseNodeType[]
  edges: BaseEdgeType[]
  // form: UseFormReturnType<Zod.infer<typeof zScenarioUpdate>>
}

const nodeTypes: NodeTypes = {
  base: BaseNode,
  ghost: GhostNode,
}

const edgeTypes: EdgeTypes = {
  default: Edge,
}

const BASE_BG_GAP = 32
const BASE_BG_STROKE = 1

function ScenarioCanvas(props: ScenarioCanvasProps) {
  const snap = useSnapshot(store)
  const [nodes, setNodes, onNodesChange] = useNodesState<BaseNodeType>(props.nodes as BaseNodeType[])
  const [edges, setEdges, onEdgesChange] = useEdgesState<BaseEdgeType>(props.edges || ([] as BaseEdgeType[]))
  const { screenToFlowPosition } = useReactFlow()
  const updateNodeInternals = useUpdateNodeInternals()
  const { id } = useParams()

  const {
    drafted,
    setDrafted,
    history,
    onLayout,
    syncHistory,
    deleteGhostNode,
    replaceGhostNode,
    createGhostNode,
    handlePublish,
    preparePublish,
    // syncForm,
    inited,
    mountNewNode,
    getSafeLabel,
  } = useScenarioHelpers({
    nodes,
    edges,
    setEdges,
    setNodes,
    // form: props.form,
    id,
  })

  return (
    <>
      <ReactFlow
        // fitView
        nodes={nodes}
        edges={edges}
        className={cn("[&_.react-flow__nodes]:opacity-0", inited && "[&_.react-flow__nodes]:opacity-100")}
        onlyRenderVisibleElements
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        onDragOver={(event) => {
          event.preventDefault()
          event.dataTransfer.dropEffect = "move"
        }}
        onDrop={(event) => {
          event.preventDefault()

          const [nodeType, actionId, operationId] = event.dataTransfer.getData("application/reactflow")?.split("::")
          if (!nodeType) return

          const position = screenToFlowPosition({
            x: event.clientX,
            y: event.clientY,
          })
          const newNode = mountNewNode({ position, nodeType: nodeType, actionId, operationId })
          setNodes((nds) => nds.concat(newNode))
          syncHistory()
          // syncForm()
        }}
        connectionLineComponent={ConnectionLine}
        connectionLineType={ConnectionLineType.Bezier}
        onConnectStart={(_, params) => {
          if (params.nodeId) {
            updateNodeInternals(params.nodeId)
          }
        }}
        onConnect={(connection) => {
          const node = nodes.find((node) => node.id === connection.source)
          const actionType = node?.data?.node_type

          if (!actionType) return

          const actionDef = store.scenario.nodeDefinitions.record[actionType]

          const isErrorEdge = connection.sourceHandle === "error_handle"

          setEdges((eds) =>
            addEdge(
              {
                ...connection,
                ...getNewEdgeProps({ color: actionDef.color, errorEdge: isErrorEdge }),
                ...(isErrorEdge
                  ? {
                      data: { startLabel: "Failed" },
                    }
                  : {}),
              } as Connection,
              eds,
            ),
          )
          syncHistory()
        }}
        // onNodesDelete={(deletedNodes) => {
        //   store.scenario.actions.setSelectedNode(null)
        //   const deletedNodeIds = deletedNodes.map((node) => node.id)

        //   console.log("deletedNodeIds", deletedNodeIds)

        //   // Remove as conexões (edges) associadas ao nó deletado
        //   setEdges((eds) => eds.filter((edge) => !deletedNodeIds.includes(edge.source) && !deletedNodeIds.includes(edge.target)))
        // }}
        // onNodeClick={(_, node) => handleNodesRemove([node.id])}
        onConnectEnd={(event, connectionState) => {
          // Created ghost node
          const fromNodeId = connectionState?.fromNode?.id

          if (fromNodeId) {
            updateNodeInternals(fromNodeId)
          }

          if (connectionState.isValid || connectionState?.fromHandle?.type === "target" || !fromNodeId) {
            return
          }

          const { clientX, clientY } = "changedTouches" in event ? event.changedTouches[0] : event

          createGhostNode({
            fromNode: connectionState.fromNode as any,
            fromHandle: connectionState.fromHandle,
            position: {
              x: clientX + 5,
              y: clientY - 40,
            },
          })
        }}
        onNodeDragStop={() => {
          syncHistory()
        }}
        onDelete={() => {
          syncHistory()
        }}
        onPaneClick={() => {
          if (store.scenario.editing.selectedNode && store.scenario.editing.selectedNode.node?.id !== "ghost") {
            store.scenario.actions.setSelectedNode(null)
          }
        }}
        onNodeClick={(_, node) => {
          if (store.scenario.editing.selectedNode?.node?.id !== "ghost") {
            store.scenario.actions.setSelectedNode({ node_type: node.data.node_type, node })
          }
        }}
      >
        {/* <Background color="#eae9e9" size={100} variant={BackgroundVariant.Lines} /> */}
        {/* <Background id="1" gap={10} color="#f1f1f1" variant={BackgroundVariant.Lines} /> */}

        <Background id="2" gap={80} color="rgba(0,0,0,0.05)" variant={BackgroundVariant.Lines} />
        <Panels
          {...props}
          onLayout={onLayout}
          drafted={drafted}
          setDrafted={setDrafted}
          history={history}
          handlePublish={handlePublish}
          nodes={nodes}
          preparePublish={preparePublish}
        />
        <BaseDrawer deleteGhostNode={deleteGhostNode} replaceGhostNode={replaceGhostNode} getSafeLabel={getSafeLabel} />
      </ReactFlow>
      {snap.scenario.editing.hoveringEdge && <EdgeRemoveButton />}
    </>
  )
}

export default ScenarioCanvas
